import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Smile, Frown, Gift, Award, Trophy, GripVertical, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ProgressBarsManager } from "@/components/ProgressBarsManager";
import { useI18n } from "@/lib/i18n";

const COLORS = ["#00C853", "#00B8D4", "#2962FF", "#AA00FF", "#FF6D00", "#FFD600", "#FF007F", "#FF3333"];

function EditorDialog({ trigger, title, fields, initial, onSave }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const save = async () => { await onSave(form); setOpen(false); };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setForm(initial); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-3xl">
        <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-sm font-bold">{f.label}</label>
              <Input data-testid={`field-${f.key}`} type={f.type || "text"} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })} className="rounded-xl mt-1 h-11" />
            </div>
          ))}
          <div>
            <label className="text-sm font-bold">{t("color")}</label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {COLORS.map((c) => <button key={c} onClick={() => setForm({ ...form, color: c })} className="w-10 h-10 rounded-full hover:scale-110 transition-transform" style={{ background: c, outline: form.color === c ? "3px solid hsl(var(--foreground))" : "none", outlineOffset: 2 }} />)}
            </div>
          </div>
        </div>
        <DialogFooter><Button data-testid="editor-save-btn" onClick={save} className="rounded-full h-12 px-6 font-bold">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Drag & drop list wrapper
function Sortable({ kind, items, reload, children }) {
  const [from, setFrom] = useState(null);
  const onDrop = async (toIdx) => {
    if (from === null || from === toIdx) return;
    const ids = items.map((i) => i.id);
    const [m] = ids.splice(from, 1);
    ids.splice(toIdx, 0, m);
    setFrom(null);
    await api.reorder(kind, ids);
    reload();
  };
  return items.map((it, i) => (
    <div key={it.id} draggable onDragStart={() => setFrom(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)} data-testid={`sortable-${it.id}`}>
      {children(it)}
    </div>
  ));
}

function ItemRow({ it, sub, onDup, onDel, onEdit }) {
  return (
    <div data-testid={`config-item-${it.id}`} className="flex items-center gap-3 p-3 rounded-2xl border-2" style={{ borderColor: it.color + "55", background: it.color + "10" }}>
      <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab shrink-0" />
      <span className="text-2xl">{it.emoji}</span>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm truncate">{it.name}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </div>
      <div className="flex gap-1">
        {onEdit}
        {onDup && <button onClick={onDup} className="w-9 h-9 rounded-xl bg-card hover:bg-accent flex items-center justify-center"><Copy className="w-4 h-4" /></button>}
        <button data-testid={`del-item-${it.id}`} onClick={onDel} className="w-9 h-9 rounded-xl bg-card hover:bg-destructive hover:text-white flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

const emptyMedal = { name: "", description: "", image: "", url: "", color: "#FFD600" };

function MedalsSection({ classId, medals, reload }) {
  const { t } = useI18n();
  const [editor, setEditor] = useState({ open: false, id: null, form: emptyMedal });
  const save = async () => {
    const f = editor.form;
    if (!f.name.trim()) return toast.error("Escribe un nombre");
    if (editor.id) await api.updateMedal(editor.id, f); else await api.createMedal({ class_id: classId, ...f });
    toast.success("Guardado"); setEditor({ open: false, id: null, form: emptyMedal }); reload();
  };
  const del = async (id) => { if (window.confirm("¿Eliminar medalla?")) { await api.deleteMedal(id); reload(); } };
  return (
    <div className="bg-card rounded-3xl border-2 border-border p-5 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-display font-extrabold text-lg flex items-center gap-2"><Trophy className="w-5 h-5 text-yellow-500" /> {t("projectMedals")}</h3>
        <Button data-testid="add-medal-btn" onClick={() => setEditor({ open: true, id: null, form: emptyMedal })} className="rounded-full h-11 px-5 font-bold gap-1"><Plus className="w-4 h-4" /> {t("newF")}</Button>
      </div>
      <p className="text-sm text-muted-foreground -mt-2">{t("medalsHint")}</p>
      {medals.length === 0 ? <p className="text-sm text-muted-foreground py-2">{t("noMedalsYet")}</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Sortable kind="medals" items={medals} reload={reload}>
            {(m) => (
              <div data-testid={`medal-${m.id}`} className="group bg-muted/50 rounded-2xl border-2 border-border p-3 flex flex-col items-center text-center relative">
                <div className="absolute top-1.5 right-1.5 flex gap-1 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`edit-medal-${m.id}`} onClick={() => setEditor({ open: true, id: m.id, form: { name: m.name, description: m.description || "", image: m.image || "", url: m.url || "", color: m.color || "#FFD600" } })} className="w-8 h-8 rounded-lg bg-card hover:bg-accent flex items-center justify-center"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(m.id)} className="w-8 h-8 rounded-lg bg-card hover:bg-destructive hover:text-white flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                {m.image ? <img src={m.image} alt={m.name} className="w-16 h-16 object-contain" /> : <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl" style={{ background: (m.color || "#FFD600") + "22" }}>❖</div>}
                <div className="font-bold text-sm mt-2">{m.name}</div>
              </div>
            )}
          </Sortable>
        </div>
      )}
      <Dialog open={editor.open} onOpenChange={(v) => !v && setEditor({ open: false, id: null, form: emptyMedal })}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle className="font-display text-xl">{editor.id ? t("edit") : t("newF")} · {t("medals")}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <Input data-testid="medal-name-input" placeholder={t("name")} value={editor.form.name} onChange={(e) => setEditor((s) => ({ ...s, form: { ...s.form, name: e.target.value } }))} className="rounded-xl h-11" />
            <Input placeholder={t("descriptionL")} value={editor.form.description} onChange={(e) => setEditor((s) => ({ ...s, form: { ...s.form, description: e.target.value } }))} className="rounded-xl h-11" />
            <Input placeholder="URL (PNG)" value={editor.form.image} onChange={(e) => setEditor((s) => ({ ...s, form: { ...s.form, image: e.target.value } }))} className="rounded-xl h-11" />
            <Input data-testid="medal-url-input" placeholder="https://…" value={editor.form.url} onChange={(e) => setEditor((s) => ({ ...s, form: { ...s.form, url: e.target.value } }))} className="rounded-xl h-11" />
          </div>
          <DialogFooter><Button data-testid="save-medal-btn" onClick={save} className="rounded-full h-12 px-6 font-bold">{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ConfigTab({ classId, cls, groups, events, rewards, badges, medals, reload }) {
  const { t } = useI18n();
  const positives = events.filter((e) => e.type === "positive");
  const negatives = events.filter((e) => e.type === "negative");

  const evFields = [{ key: "name", label: t("name") }, { key: "emoji", label: t("emoji") }, { key: "points", label: t("points"), type: "number" }];
  const rwFields = [{ key: "name", label: t("name") }, { key: "emoji", label: t("emoji") }, { key: "description", label: t("descriptionL") }, { key: "cost", label: t("cost"), type: "number" }];
  const bdFields = [{ key: "name", label: t("name") }, { key: "emoji", label: t("emoji") }, { key: "description", label: t("descriptionL") }, { key: "category", label: t("category") }];

  const createEvent = (type) => async (f) => { await api.createEvent({ class_id: classId, type, ...f }); toast.success("Creado"); reload(); };
  const updateEvent = (id) => async (f) => { await api.updateEvent(id, f); toast.success("Guardado"); reload(); };
  const createReward = async (f) => { await api.createReward({ class_id: classId, ...f }); toast.success("Creada"); reload(); };
  const updateReward = (id) => async (f) => { await api.updateReward(id, f); toast.success("Guardado"); reload(); };
  const createBadge = async (f) => { await api.createBadge({ class_id: classId, ...f }); toast.success("Creado"); reload(); };
  const updateBadge = (id) => async (f) => { await api.updateBadge(id, f); toast.success("Guardado"); reload(); };

  const editBtn = <button className="w-9 h-9 rounded-xl bg-card hover:bg-accent flex items-center justify-center"><Pencil className="w-4 h-4" /></button>;

  const Section = ({ icon: Icon, color, title, children, add }) => (
    <div className="bg-card rounded-3xl border-2 border-border p-5 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display font-extrabold text-lg flex items-center gap-2"><Icon className="w-5 h-5" style={{ color }} /> {title}</h3>
        {add}
      </div>
      {children}
    </div>
  );

  return (
    <div className="space-y-6">
      <ProgressBarsManager classId={classId} bars={cls?.progress_bars || []} groups={groups || []} reload={reload} />
      <MedalsSection classId={classId} medals={medals || []} reload={reload} />
      <p className="text-sm text-muted-foreground">{t("dragHint")}</p>
      <div className="grid lg:grid-cols-2 gap-6">
        <Section icon={Smile} color="#10b981" title={t("positivePoints")}
          add={<EditorDialog title={t("positivePoints")} fields={evFields} initial={{ name: "", emoji: "✓", points: 1, color: COLORS[0] }} onSave={createEvent("positive")} trigger={<Button data-testid="add-positive-btn" className="rounded-full gap-1 h-10 px-4 font-bold"><Plus className="w-4 h-4" /> {t("newM")}</Button>} />}>
          <div className="space-y-2">
            <Sortable kind="events" items={positives} reload={reload}>
              {(e) => <ItemRow it={e} sub={`+${e.points} pts`} onDel={async () => { await api.deleteEvent(e.id); reload(); }} onDup={async () => { await api.duplicateEvent(e.id); reload(); }}
                onEdit={<EditorDialog title={t("edit")} fields={evFields} initial={e} onSave={updateEvent(e.id)} trigger={editBtn} />} />}
            </Sortable>
          </div>
        </Section>

        <Section icon={Frown} color="#ef4444" title={t("negativePoints")}
          add={<EditorDialog title={t("negativePoints")} fields={evFields} initial={{ name: "", emoji: "✕", points: -1, color: "#FF3333" }} onSave={createEvent("negative")} trigger={<Button data-testid="add-negative-btn" className="rounded-full gap-1 h-10 px-4 font-bold"><Plus className="w-4 h-4" /> {t("newM")}</Button>} />}>
          <div className="space-y-2">
            <Sortable kind="events" items={negatives} reload={reload}>
              {(e) => <ItemRow it={e} sub={`${e.points} pts`} onDel={async () => { await api.deleteEvent(e.id); reload(); }} onDup={async () => { await api.duplicateEvent(e.id); reload(); }}
                onEdit={<EditorDialog title={t("edit")} fields={evFields} initial={e} onSave={updateEvent(e.id)} trigger={editBtn} />} />}
            </Sortable>
          </div>
        </Section>

        <Section icon={Gift} color="#d946ef" title={t("rewardsTitle")}
          add={<EditorDialog title={t("rewardsTitle")} fields={rwFields} initial={{ name: "", emoji: "❍", description: "", cost: 10, color: "#AA00FF" }} onSave={createReward} trigger={<Button data-testid="add-reward-btn" className="rounded-full gap-1 h-10 px-4 font-bold"><Plus className="w-4 h-4" /> {t("newF")}</Button>} />}>
          <div className="space-y-2">
            <Sortable kind="rewards" items={rewards} reload={reload}>
              {(r) => <ItemRow it={r} sub={`${r.cost} pts`} onDel={async () => { await api.deleteReward(r.id); reload(); }}
                onEdit={<EditorDialog title={t("edit")} fields={rwFields} initial={r} onSave={updateReward(r.id)} trigger={editBtn} />} />}
            </Sortable>
          </div>
        </Section>

        <Section icon={Award} color="#eab308" title={t("badgesTitle")}
          add={<EditorDialog title={t("badgesTitle")} fields={bdFields} initial={{ name: "", emoji: "★", description: "", category: "General", color: "#FFD600" }} onSave={createBadge} trigger={<Button data-testid="add-badge-btn" className="rounded-full gap-1 h-10 px-4 font-bold"><Plus className="w-4 h-4" /> {t("newM")}</Button>} />}>
          <div className="space-y-2">
            <Sortable kind="badges" items={badges} reload={reload}>
              {(b) => <ItemRow it={b} sub={b.category} onDel={async () => { await api.deleteBadge(b.id); reload(); }}
                onEdit={<EditorDialog title={t("edit")} fields={bdFields} initial={b} onSave={updateBadge(b.id)} trigger={editBtn} />} />}
            </Sortable>
          </div>
        </Section>
      </div>
    </div>
  );
}
